if (!customElements.get('countdown-timer')) {
  customElements.define(
    'countdown-timer',
    class CountdownTimer extends HTMLElement {
      constructor() {
        super();

        this.init();
      }

      disconnectedCallback() {
        clearInterval(this.intervalId);
      }

      init() {
        this.MS_IN_SEC = 1000;
        this.MS_IN_MIN = 60 * this.MS_IN_SEC;
        this.MS_IN_HRS = 60 * this.MS_IN_MIN;
        this.MS_IN_DAY = 24 * this.MS_IN_HRS;

        this.selectorMapping = {
          day: '[data-timer-day]',
          hour: '[data-timer-hour]',
          minute: '[data-timer-minute]',
          second: '[data-timer-second]',
        };
        this.units = ['day', 'hour', 'minute', 'second'];
        this.type = this.dataset.type;
        this.duration = this.dataset.duration;
        this.loop = false;
        this.shouldAddLeadingZero = true;

        this.initCountdown();
      }

      initCountdown() {
        this.targetTime =
          this.type === 'fixed_time' ? this.resolveFixedTargetTime() : this.getEvergreenDueDate().getTime();

        if (!this.targetTime || this.targetTime <= Date.now()) {
          this.hideCountdownAndParent();
          return;
        }

        this.fetchDomElements();
        this.beginCountdown();
      }

      // Merchants enter a wall-clock date/time (e.g. "2025/08/20 03:45 PM") meant in the
      // store's time zone. `new Date(string)` parses that as the *visitor's* device time
      // zone instead, so the same countdown shows a different deadline to every visitor.
      // We parse the string manually and resolve it against shop.timezone (passed in via
      // data-timezone) so every visitor counts down to the same real-world instant.
      resolveFixedTargetTime() {
        // Prefer the unix timestamp resolved server-side by Liquid (store timezone,
        // authoritative). The client-side parsing below is only a fallback.
        const dueTs = parseInt(this.dataset.dueTs, 10);
        if (!isNaN(dueTs) && dueTs > 0) return dueTs * 1000;

        const rawDueDate = this.dataset.dueDate;
        if (!rawDueDate) return null;

        const components = this.parseDueDate(rawDueDate);
        if (!components) return null;

        const utcGuess = Date.UTC(
          components.year,
          components.month - 1,
          components.day,
          components.hour,
          components.minute,
          components.second
        );

        const timeZone = this.dataset.timezone;
        if (!timeZone) return utcGuess;

        try {
          const offset = this.getTimeZoneOffsetMs(timeZone, utcGuess);
          return utcGuess - offset;
        } catch (error) {
          return utcGuess;
        }
      }

      parseDueDate(value) {
        const match = value
          .trim()
          .match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?)?$/i);

        if (!match) return null;

        const [, year, month, day, hour, minute, second, meridiem] = match;
        let hour24 = hour ? parseInt(hour, 10) : 0;

        if (meridiem) {
          hour24 %= 12;
          if (meridiem.toUpperCase() === 'PM') hour24 += 12;
        }

        return {
          year: parseInt(year, 10),
          month: parseInt(month, 10),
          day: parseInt(day, 10),
          hour: hour24,
          minute: minute ? parseInt(minute, 10) : 0,
          second: second ? parseInt(second, 10) : 0,
        };
      }

      // Offset (ms) between UTC and `timeZone` at approximately `utcGuessMs`, computed via
      // Intl instead of a timezone-data library, and DST-correct for the given date.
      getTimeZoneOffsetMs(timeZone, utcGuessMs) {
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone,
          hourCycle: 'h23',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        });

        const parts = formatter.formatToParts(new Date(utcGuessMs)).reduce((acc, part) => {
          if (part.type !== 'literal') acc[part.type] = parseInt(part.value, 10);
          return acc;
        }, {});

        const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
        return asUtc - utcGuessMs;
      }

      fetchDomElements() {
        this.elements = {};
        this.units.forEach((unit) => {
          this.elements[unit] = this.querySelector(this.selectorMapping[unit]);
        });
      }

      beginCountdown() {
        this.intervalId = setInterval(() => {
          const remainingTime = this.targetTime - Date.now();
          if (remainingTime <= 0) {
            this.terminateCountdown();
          } else {
            this.updateDisplay(remainingTime);
          }
        }, 1000);
        this.classList.remove('hidden');
      }

      updateDisplay(remainingTime) {
        const timeComponents = this.computeTimeComponents(remainingTime);
        this.units.forEach((unit) => {
          if (this.elements[unit]) {
            this.elements[unit].textContent = this.formatTimeComponent(timeComponents[unit]);
          }
        });
      }

      computeTimeComponents(ms) {
        return {
          day: Math.floor(ms / this.MS_IN_DAY),
          hour: Math.floor((ms % this.MS_IN_DAY) / this.MS_IN_HRS),
          minute: Math.floor((ms % this.MS_IN_HRS) / this.MS_IN_MIN),
          second: Math.floor((ms % this.MS_IN_MIN) / this.MS_IN_SEC),
        };
      }

      formatTimeComponent(value) {
        return this.shouldAddLeadingZero && value < 10 ? `0${value}` : value.toString();
      }

      terminateCountdown() {
        clearInterval(this.intervalId);
        if (this.loop) {
          this.beginCountdown();
        } else {
          this.hideCountdownAndParent();
        }
      }

      // Hides the timer numbers, and optionally a containing wrapper (via data-hide-target,
      // a selector matched with .closest()) so callers can hide an entire "Sale ends in..."
      // slide instead of leaving orphaned text/CTA once the numbers disappear.
      hideCountdownAndParent() {
        this.classList.add('hidden');
        const hideTargetSelector = this.dataset.hideTarget;
        const hideTarget = hideTargetSelector ? this.closest(hideTargetSelector) : null;
        if (hideTarget) hideTarget.classList.add('hidden');
      }

      getEvergreenDueDate() {
        let intervalHrs;
        const now = new Date();
        const dueDate = new Date(now);

        switch (this.duration) {
          case 'every_month':
            dueDate.setMonth(dueDate.getMonth() + 1); // Next month.
            dueDate.setDate(1); // Start from the 1st day of the next month.
            dueDate.setHours(0, 0, 0, 0);
            return dueDate;
          case 'every_week':
            const dayOfWeek = now.getDay();
            const daysUntilNextWeek = 7 - dayOfWeek;
            dueDate.setDate(now.getDate() + daysUntilNextWeek); // Move to next Sunday.
            dueDate.setHours(0, 0, 0, 0); // Set the time to 00:00:00.
            return dueDate;
          case 'every_2_hrs':
            intervalHrs = 2;
            break;
          case 'every_6_hrs':
            intervalHrs = 6;
            break;
          case 'every_12_hrs':
            intervalHrs = 12;
            break;
          case 'every_24_hrs':
            intervalHrs = 24;
            break;
        }

        const currentHour = now.getHours();

        // Possible start points based on the interval.
        const startPoints = [];
        for (let i = 0; i < 24; i += intervalHrs) {
          startPoints.push(i);
        }

        // Find the next start point.
        for (let i = 0; i < startPoints.length; i++) {
          if (
            currentHour < startPoints[i] ||
            (currentHour === startPoints[i] && now.getMinutes() === 0 && now.getSeconds() === 0)
          ) {
            dueDate.setHours(startPoints[i], 0, 0, 0);
            return dueDate;
          }
        }

        // If no future point today, roll over to the first point tomorrow.
        dueDate.setDate(dueDate.getDate() + 1);
        dueDate.setHours(startPoints[0], 0, 0, 0);
        return dueDate;
      }
    }
  );
}
